<template>
    <div id="calendar-events">
        <div class="route-page-content breadcrumb-content">
            <SidebarChild
                v-if="showSideMenu"
                :items="menuItems"
                :selected-item="currentPage"
                :focusActive="true"
                @selected="fnClicked"
                @clicked="fnClicked"
            />
            <div class="page-content-wrapper">
                <div class="calendar-events-wrapper">
                    <CalendarEventsToolbox
                        :selected-calendar-id="selectedCalendarId"
                        :mode="mode"
                        @changeMode="(_mode) => (mode = _mode)"
                        @calendarChange="(id) => (selectedCalendarId = id)"
                        :type="type"
                        :reservationPlans="reservationPlans"
                    />
                    <a-divider />
                    <div class="main">
                        <div class="left">
                            <vc-date-picker v-model="selectedDate" color="red" is-dark />
                        </div>

                        <div class="events-wrapper">
                            <CalendarEventListview
                                :events="events"
                                :group-by-date="true"
                                v-if="events.length"
                                :calendarList="calendarList"
                            />
                            <NoMessage v-else :desc="$t('calendar.eventNotFound')" />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations, mapState } from "vuex";
import { log2 } from "../../../../../utils/log2";
import VueDatepickerLocal from "vue-datepicker-local";
import moment from "moment";
import FullCalendar from "@fullcalendar/vue";
import _ from "lodash";
import CreateEventModal from "../CreateEventModal.vue";
import NoMessage from "../../../Mail/NoMessage.vue";
import CalendarEventsToolbox from "./CalendarEventsToolbox.vue";
import { CALENDAR_API } from "../../../../../api/calendar";
import CalendarEventListview from "./CalendarEventListview.vue";
import SidebarChild from "../../../../../components/Sidebar/SidebarChild.vue";
import getCalendarMenuItems from "../calendarMenuItems";

const log = log2("CalendarEvents");
export default {
    name: "CalendarEvents",
    components: {
        SidebarChild,
        CalendarEventListview,
        CalendarEventsToolbox,
        NoMessage,
        CreateEventModal,
        VueDatepickerLocal,
        FullCalendar,
    },
    props: {
        type: {
            type: String,
            default: "calendar",
        },
        showSideMenu: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            selectedCalendarId: "my",
            mode: "upcoming",
            eventModalVisible: false,
            calendarList: [],
            events: [],
            selectedDate: null,
            loadingInstance: null,
            loading: false,
            eventDetail: {},
            reservationPlans: [],
            currentPage: this.type === "calendar" ? "events" : "reservations",
        };
    },
    computed: {
        menuItems() {
            return getCalendarMenuItems();
        },
        ...mapState({
            site: (state) => state.site.site,
            user: (state) => state.auth.user,
            users: (state) => state.user.users.data,
        }),
        ...mapGetters({}),
    },
    methods: {
        ...mapActions({
            getCalendarList: "calendar/getCalendarList",
        }),
        ...mapMutations({}),
        async getEvents() {
            console.log("getEvents");
            const data = {
                userIds: [],
                startDate: undefined,
                endDate: undefined,
            };
            if (this.mode === "upcoming") {
                data.startDate = moment().startOf("day").utc().toISOString();
                data.endDate = undefined;
            } else if (this.mode === "past") {
                data.startDate = undefined;
                data.endDate = moment().utc().toISOString();
            } else {
                data.startDate = moment(this.selectedDate).startOf("day").utc().toISOString();
                data.endDate = moment(this.selectedDate).endOf("day").utc().toISOString();
            }

            if (this.selectedCalendarId === "my") {
                data.userIds.push(this.user._id);
            } else if (this.selectedCalendarId === "everyone") {
                data.userIds = this.users.map((user) => user._id);
            } else {
                data.userIds.push(this.selectedCalendarId);
            }
            this.loading = true;
            this.loadingInstance.show();

            if (this.type === "reservation") {
                CALENDAR_API.getReservationEvents(this.selectedCalendarId, data.startDate, data.endDate, this.user._id)
                    .then((events) => {
                        this.events = events;
                    })
                    .finally(() => {
                        this.loading = false;
                        this.loadingInstance.close();
                    });
            } else {
                CALENDAR_API.listEvents(data.userIds, data.startDate, data.endDate)
                    .then((events) => {
                        this.events = events;
                    })
                    .finally(() => {
                        this.loading = false;
                        this.loadingInstance.close();
                    });
            }
        },
        fnClicked(item) {
            if (item.key === "calendar") {
                this.$router.push("/calendar");
            } else if (item.key === "reservationBuilder") {
                this.$router.push("/reservation/landing-page");
            } else if (item.key === "events") {
                this.$router.push("/calendar/events");
            } else if (item.key === "reservations") {
                this.$router.push("/calendar/reservations");
            }
        },
    },
    async mounted() {
        this.loadingInstance = this.$veLoading({
            target: document.querySelector(".events-wrapper"),
            name: "wave",
        });

        CALENDAR_API.getReservationPlans().then((res) => {
            this.reservationPlans = res;
        });

        this.calendarList = await this.getCalendarList({ userId: this.user._id, userName: this.user.name });
        await this.getEvents();
    },
    beforeDestroy() {},
    watch: {
        selectedDate: {
            handler: function (val) {
                log("selectedDate changed", val);
                if (val) {
                    this.mode = "custom";
                }
                this.getEvents();
            },
            deep: true,
        },
        selectedCalendarId() {
            this.getEvents();
        },
        mode() {
            this.getEvents();
        },
    },
};
</script>

<style lang="scss" scoped>
#calendar-events {
    .calendar-events-wrapper {
        display: flex;
        width: 100%;
        flex-direction: column;
        gap: 5px;
        padding: 10px;
    }

    .vue-avatar--wrapper {
        font-size: 12px !important;
    }

    .ant-divider-horizontal {
        margin: 5px 0;
    }

    .main {
        display: flex;
        gap: 10px;

        .left {
            width: 260px;
        }

        .events-wrapper {
            width: calc(100% - 260px);
        }
    }
}
</style>
<style lang="scss">
#calendar-events {
    .vue-avatar--wrapper {
        font-size: 12px !important;
    }
}
</style>
